/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import lizard from 'assets/Loader_Logo_2.gif'; // KeyImage.png'; // lizard.svg';
import { colors } from 'styles/variables';
import 'services/elementRemovePolyfill';
import { getCache } from 'services/imageCache';
import { State } from 'store/store';
import { useSelector } from 'react-redux';
import pubSub from 'services/pubSub';
import hexToRGB from 'services/hexToRGB';

const { lGrey1, mint } = colors;
const moduleImagesPreloaderDomContainerId = 'module-images-preloader';

// Image loading creates a new promise and wraps the DOM based
// image loading element. IF there is an error, we just mark it as resolved and
// move on, however we probably want to adjust this in the future to report on
// possible failures
const loadImage = (src, cb): Promise<any> => {
  return new Promise(resolve => {
    const img = new Image();
    img.src = src;

    img.onload = () => {
      cb(src);
      resolve(src);
    };

    img.onerror = () => {
      cb(src);
      resolve(src);
    };
  });
};

interface IModuleImagesPreloader {
  images: any[];
}

const ModuleImagesPreloader = ({ images }: IModuleImagesPreloader) => {
  //
  const [imgsLoaded, setImgsLoaded] = useState(false);
  const [whichImagesLoaded, setImgAsLoaded] = useState<String[]>([]);
  const [minDisplayTime, setMinDisplayTime] = useState(false);
  const [boot, setBoot] = useState(false);

  const [showEditPreloader, setShowEditPreloader] = useState<boolean>(false);

  let giveFocusToFirstSectionOnLoad = useSelector((state: State) => {
    return state?.theme?.giveFocusToFirstSectionOnLoad;
  });

  // if we're launching an older module that won't yet have the entry on theme
  // undefined is a true
  if (giveFocusToFirstSectionOnLoad === undefined) {
    giveFocusToFirstSectionOnLoad = true;
  }

  // giveFocusToFirstSectionOnLoad = false; // for patching

  const modulePreloaderSettings = useSelector((state: State) => {
    const AdvancedEditing = Object.values(state.blocks).find(b => {
      return b.type === 'AdvancedEditing';
    });

    if (AdvancedEditing) {
      return AdvancedEditing.props?.modulePreloaderSettings || {};
    }

    return {};
  });

  const {
    modulePreloaderbackgroundColor = '#fff',
    // progressBarColor = mint,
    moduleLoadingMessage = 'Module loading',
  } = modulePreloaderSettings;

  let progressBarColor = modulePreloaderSettings.progressBarColor || mint;
  const { r, g, b } = hexToRGB(progressBarColor);

  let src = lizard;

  if (modulePreloaderSettings.src) {
    src = modulePreloaderSettings.src;
  }

  const preLoader = useRef<any>(null);
  const documentTitle = useRef<string>(document.title);

  const moduleImagesPreloaderDomContainer = useRef(
    document.querySelector<HTMLDivElement>(
      `#${moduleImagesPreloaderDomContainerId}`
    )
  );

  if (!boot && !moduleImagesPreloaderDomContainer.current) {
    moduleImagesPreloaderDomContainer.current = document.createElement(
      'div'
    ) as HTMLDivElement;

    moduleImagesPreloaderDomContainer.current.id = moduleImagesPreloaderDomContainerId;

    moduleImagesPreloaderDomContainer.current.style.position = 'fixed';
    moduleImagesPreloaderDomContainer.current.style.left = '0px';
    moduleImagesPreloaderDomContainer.current.style.top = '0px';
    moduleImagesPreloaderDomContainer.current.style.width = '100vw';
    moduleImagesPreloaderDomContainer.current.style.height = '100vh';
    moduleImagesPreloaderDomContainer.current.style.zIndex = '2147483647';
    //
    document.body.appendChild(moduleImagesPreloaderDomContainer.current);
  }

  useEffect(() => {
    const root = document.querySelector<HTMLDivElement>('#root');

    if (!boot && root) {
      document.title = 'Module loading';
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('tabindex', '-1');

      return;
    }

    document.title = documentTitle.current;
    root?.setAttribute('aria-hidden', 'false');
    root?.setAttribute('tabindex', '0');

    if (giveFocusToFirstSectionOnLoad === false) {
      if (document.activeElement) {
        (document.activeElement as HTMLElement)?.blur();
      }

      console.log('DO NOT GIVE CONTENT FOCUS', document.activeElement);

      return;
    }

    const sections = Array.from(
      document.querySelectorAll('section')
    ) as HTMLElement[];

    if (sections && sections[0]) {
      // might need to do something here if the page jumps to top
      // from giving first interaction focus
      //

      if (!window.location.hash) {
        const modalContainer = document.querySelector(
          '#modal-container'
        ) as HTMLElement;

        if (modalContainer?.hasChildNodes()) {
          // console.log('modal is active. do not take focus away from modal');
          return;
        }

        //
        sections[0].focus();
      }

      // if we have a hash and have navigated to it
      // we should give IT focus
      if (window.location.hash) {
        const section = sections.find(section => {
          return section.id === window.location.hash.replace('#', '');
        });

        if (section) {
          section.focus();
          return;
        }
      }

      //
      sections[0].focus();
    }

    return () => {
      // set the focus back to root ?
      // is doing it above. double tap it maybe ?
    };
  }, [boot]);

  // Show the loader for at least 1000 ms
  useEffect(() => {
    setTimeout(() => {
      !minDisplayTime && setMinDisplayTime(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (!imgsLoaded) {
      console.time('TIME TO IMAGES LOADED');
    } else {
      console.timeEnd('TIME TO IMAGES LOADED');
    }
  }, [imgsLoaded]);

  useEffect(() => {
    whichImagesLoaded.length === images.length && setImgsLoaded(true);
  }, [whichImagesLoaded]);

  // Preload Images on load
  useEffect(() => {
    const fun = async () => {
      const cacheAvailable = 'caches' in self;

      if (!cacheAvailable) {
        images.map((imgSrc: string) => {
          return loadImage(imgSrc, () => {
            setImgAsLoaded(whichImagesLoaded => [...whichImagesLoaded, imgSrc]);
          });
        });

        return;
      }

      const cache = await getCache();

      images.map(async (imgSrc: string) => {
        const resp = await cache.match(imgSrc);

        if (!resp) {
          try {
            await cache.add(imgSrc);
          } catch (err) {
            console.log(err);
          }
        }

        setImgAsLoaded(whichImagesLoaded => [...whichImagesLoaded, imgSrc]);
      });

      return;
    };

    fun();
  }, []);

  // Fade out the loading screen after the fade out is completed,
  // remove the loading screen from the DOM
  useEffect(() => {
    if (!preLoader.current) {
      return;
    }

    const fader = () => {
      setBoot(true);
    };

    preLoader.current?.addEventListener('transitionend', fader);

    return () => {
      preLoader.current?.removeEventListener('transitionend', fader);
    };
  }, []);

  useEffect(() => {
    // do the polyfill for ie ?

    if (boot) {
      moduleImagesPreloaderDomContainer.current?.remove();
    }
  }, [boot]);

  useEffect(() => {
    const pS = pubSub.subscribe(
      'SHOW_EDIT_PRELOADER',
      ({ showEditPreloader }) => {
        setShowEditPreloader(showEditPreloader);
      }
    );

    return () => {
      setShowEditPreloader(false);
      pS();
    };
  }, []);

  const [mockPercentLoaded, setMockPercentLoaded] = useState<number>(0);

  useEffect(() => {
    if (!window.dev() || !showEditPreloader) {
      return;
    }

    let tick = setInterval(() => {
      setMockPercentLoaded(Math.round(Math.random() * 100));
    }, 1000);

    return () => {
      clearInterval(tick);
    };
  }, [showEditPreloader]);

  const percentLoaded = window.dev()
    ? mockPercentLoaded
    : (whichImagesLoaded.length / images.length) * 100;

  if (boot || !moduleImagesPreloaderDomContainer.current) {
    return null;
  }

  let opacity = `opacity: ${imgsLoaded ? 0 : 1};`;

  if (window.dev()) {
    if (!showEditPreloader) {
      // console.log('hide preloader. return null');

      if (moduleImagesPreloaderDomContainer.current) {
        moduleImagesPreloaderDomContainer.current.style.display = 'none';
      }
      return null;
    } else {
      // console.log('show preloader');

      if (moduleImagesPreloaderDomContainer.current) {
        moduleImagesPreloaderDomContainer.current.style.display = '';
      }
      opacity = '';
    }
  }

  return createPortal(
    <div
      ref={preLoader}
      css={css`
        position: fixed;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        left: 0;
        top: 0;
        width: 100vw;
        height: 100vh;
        background: ${modulePreloaderbackgroundColor};

        ${opacity}

        transition: opacity 0.35s;
        z-index: 2147483638;
      `}
    >
      <img
        src={src}
        alt=""
        css={css`
          max-width: 265px;
        `}
      />

      <div
        css={css({
          width: 250,
          fontFamily: 'Poppins, Sans serif',
          fontWeight: 200,
          fontSize: 22,
        })}
      >
        <div
          css={css({
            textAlign: 'center',
            textTransform: 'capitalize',
            color: progressBarColor,
          })}
          tabIndex={0}
          aria-label={moduleLoadingMessage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percentLoaded)}
          role="progressbar"
        >
          {moduleLoadingMessage}
        </div>

        <div
          css={css({
            width: '100%',
            height: 15,
            margin: '30px 0 0 0',
            borderRadius: 9999,
            overflow: 'hidden',
            background: `rgba(${r}, ${g}, ${b}, 0.2)`,
          })}
        >
          <div
            style={{
              width: `${percentLoaded}%`,
              height: 'inherit',
              borderRadius: 9999,
              background: progressBarColor,
              transition: 'width 0.5s',
            }}
          />
        </div>
      </div>
    </div>,
    moduleImagesPreloaderDomContainer.current
  );
};

export default ModuleImagesPreloader;
